import * as Ably from 'ably';
import Constants from './Constants';
import RTPaaSError from './RTPaaSError';
import CALLING_MP3 from "../../audio/calling.mp3";
import initInternationalization from "../i18nSetup";

export default class RTPaaS {
	constructor(config, ablyOptions) {
		initInternationalization(config?.languageOfInterface);

		this.addGenericErrorHandler();
		this.config = config;
		try {
			ablyOptions.environment = "pearsonschoolassessments";
			ablyOptions.fallbackHosts = [
				"pearsonschoolassessments-a-fallback.ably-realtime.com",
				"pearsonschoolassessments-b-fallback.ably-realtime.com",
				"pearsonschoolassessments-c-fallback.ably-realtime.com",
				"pearsonschoolassessments-d-fallback.ably-realtime.com",
				"pearsonschoolassessments-e-fallback.ably-realtime.com",
			];
			ablyOptions.echoMessages = false;
			this.ably = new Ably.Realtime.Promise(ablyOptions);
			this.isAblyConnected = false;
			this.ablyStateChangeListener();
			this.errorChannel = this.createOrGetChannel("rtp:error");
			this.receivingCallAudio = new Audio(CALLING_MP3);

			this.publishMessageCount = 0;
			this.publishMessageWithoutHistoryCount = 0;
			this.publishStatsInterval = null;
			this.logPublishedMessages = true;

			//Important to track the number of messages being published with and without history,
			// to identify any potential issues with message publishing or history retrieval.
			// Set up an interval to log the publish stats every 6 seconds
			if(this.logPublishedMessages) {
				this.publishStatsInterval = setInterval(() => {
					console.log('Publish Stats:', {
						publishMessage: this.publishMessageCount,
						publishMessageWithoutHistory: this.publishMessageWithoutHistoryCount,
						total: this.publishMessageCount + this.publishMessageWithoutHistoryCount,
						isProctor: this.isProctor()
					});
				}, 6000);
			}

		} catch(error) {
			RTPaaSError.triggerAblyError(error);
			this.sendErrorMessage("RTpaaS.constructor() "+error.message+" fileName="+error.filename
				+" line#="+error.lineno+" colno="+error.colno, (new Error()).stack);
		}
	}

	// Returns the default monitoring configurations
	getDefaultMonitoringConfigs() {
		return {
			monitoringType: Constants.MonitoringType.BY_VIDEO,
			videoQuality: Constants.VideoQualityTypes.HIGH,
			videoPrefix: Constants.Prefixes.VIDEO_PREFIX,
			imagePrefix: Constants.Prefixes.IMAGE_PREFIX
		};
	}

	addGenericErrorHandler() {
		let that = this;
		window.addEventListener("error", function(event) {
			if (that.errorChannel) {
				that.sendErrorMessage(event.message, event.error.stack);
			}
		});
	}

	//it's an interface method, should be overridden in Proctor and Tester classes
	networkIssueDetected() {
		//to be overridden
	}

	//it's an interface method, should be overridden in Proctor and Tester classes
	onConnection(restoreHistoryDate) {
		//to be overridden
	}

	//it's an interface method, should be overridden in Proctor and Tester classes
	isProctor (){
		//to be overridden
		return false;
	}

	//listen to ably connection state changes
	ablyStateChangeListener(){
		this.ably?.connection.on((stateChange) => {
			if (stateChange.current === 'disconnected') {
				this.isAblyConnected = false;
				if (this.isProctor()) {
					this.networkIssueDetected();
				}
				if (!this.disconnectDate) {
					this.disconnectDate = new Date();
					this.disconnectDate.setSeconds(this.disconnectDate.getSeconds() + 1);
				}
			} else if (stateChange.current === 'connected') {
				let restoreHistory = false;
				if (this.isAblySuspended && this.disconnectDate) {
					restoreHistory = true;
				}
				this.isAblyConnected = true;
				this.isAblySuspended = false;
				if (restoreHistory) {
					this.onConnection(this.disconnectDate);
					for (let channelIdentifier of this.unsentMessageArray.keys()) {
						this.publishMessage(channelIdentifier, this.unsentMessageArray.get(channelIdentifier));
					}
				} else {
					this.onConnection();
				}
				this.disconnectDate = null;
			} else if(['suspended', 'failed'].includes(stateChange.current)){
				this.isAblySuspended = true;
				RTPaaSError.triggerAblyError(this.ably?.connection.errorReason);
				this.sendErrorMessage("ablyStateChangeListener() "+ this.ably?.connection.errorReason.message, (new Error()).stack);
			}
		});
	}

	notifyStudentExit() {
		window.dispatchEvent(new CustomEvent('rtpStudentExit', { detail: { reason: 'studentStreamLost' } }));
		console.log('a force exit event was dispatched with reason: studentStreamLost');
	}

	startNoProctorAvailableTimeout(timeoutInSeconds) {
		this.noProctorAvailableTimeout = setTimeout(() => {
			window.dispatchEvent(new CustomEvent('rtpStudentExit', { detail: { reason: 'noProctorAvailable' } }));
			console.log('a force exit event was dispatched with reason: noProctorAvailable');
			this.noProctorAvailableTimeout = null;
		}, timeoutInSeconds * 1000);
	}

	clearNoProctorAvailableTimeout() {
		clearTimeout(this.noProctorAvailableTimeout);
		this.noProctorAvailableTimeout = null;
	}

	establishAblyConnection() {
		//return a promise, which is resolved once ably is connected
		return this.ably?.connection.once('connected');
	}

	createOrGetChannel(channelIdentifier){
		let that = this;
		let channel = this.ably?.channels.get(channelIdentifier);
		channel.on('failed', function() {
			RTPaaSError.triggerAblyError(channel.errorReason);
			that.sendErrorMessage("createOrGetChannel() "+ channel.errorReason.message, (new Error()).stack);
		});
		return channel;
	}

	createChannelWithoutHistory(channelIdentifier) {
		this.createOrGetChannel(channelIdentifier);
	}

	async createChannelWithHistory(channelIdentifier) {
		let channel = this.getChannelWithHistory(channelIdentifier);
        if (this.initPresenceHandlers !== undefined) {
			await this.initPresenceHandlers(channel);
		}
		await new Promise(resolve => setTimeout(resolve, 1000));
		await channel.presence.enter({'isProctor' : this.isProctor(), 'testerIdentifier' : channelIdentifier});
		return channel;
	}

	subscribeToChannelWithoutHistory(channelIdentifier, messageName, messageHandler) {
		let channel = this.createOrGetChannel(channelIdentifier);
		this.subscribe(channel, messageName, messageHandler);
	}

	subscribeToChannel(channelIdentifier, messageName, messageHandler) {
        this.subscribe(this.getChannelWithHistory(channelIdentifier), messageName, messageHandler);
	}

	subscribe(channel, messageName, messageHandler) {
		// Unsubscribe first, to ensure we don't subscribe multiple times.
		channel.unsubscribe(messageName);
		channel.subscribe(messageName, (message) => {
			messageHandler(message);
		});
	}

	publishMessageWithoutHistory(channelIdentifier, message) {
		this.publishMessageWithoutHistoryCount++;
		//Important log to identify which message is making ably to exceed the limit.
		//console.log("publishMessageWithoutHistory: ", channelIdentifier, message);
		this.createOrGetChannel(channelIdentifier).publish(message);
	}

	publishMessage(channelIdentifier, message) {
		if (!this.isAblySuspended) {
			this.publishMessageCount++;
			//Important log to identify which message is making ably to exceed the limit.
			//console.log("publishMessage: ", channelIdentifier, message);
			this.getChannelWithHistory(channelIdentifier).publish(message);
		} else {
			if (!this.unsentMessageArray) {
				this.unsentMessageArray = new Map();
			}
			this.unsentMessageArray.set(channelIdentifier, message);
		}
	}

	getChannelWithHistory(channelIdentifier) {
		return this.createOrGetChannel(Constants.Prefixes.RTP_CHANNEL_PREFIX.concat(channelIdentifier));
	}

	sendErrorMessage(errorMessage, stackTrace) {
		if (this.errorChannel) {
			if (!stackTrace) {
				stackTrace = "";
			}
			let message = {"errorMessage" : errorMessage, "messageName" : Constants.MessageNames.ERRMSG, "eventType" : Constants.EventTypes.ERRMSG, "stack" : stackTrace.substring(0,5000),  "details" : this.toString()};
			this.errorChannel.publish(RTPaaS.createMessage(message));
		}
	}

	retrieveHistory(testerIdentifier, historyLimit) {
		let date = new Date();
		if (!historyLimit) {
			historyLimit = Constants.RetrieveHistory.DEFAULT_LIMIT_MINUTE;
		}
		date.setMinutes(date.getMinutes() - historyLimit);
		return this.retrieveHistoryFromDate(testerIdentifier, date);
	}

	retrieveHistoryFromDate(testerIdentifier, lastSuccessDate) {
		let channel = this.getChannelWithHistory(testerIdentifier);
		let that = this;
		let history = [];
		return new Promise((resolve, reject) =>  {
			//there will always be at least 1 historical message, since we aren't retrieving history until after the INITIATE_CHAT message is sent.
			channel.history({'direction' : 'forwards', 'limit' : 2}).then((paginatedResult) => {
				let message = paginatedResult.items[0];
				let numberOfHistoricalMessages = paginatedResult.items.filter(item => item.data.eventType !== Constants.EventTypes.INITIATE_CHAT).length;
				let noHistory = !message || numberOfHistoricalMessages < 1;
				if(noHistory) {
					reject('History: No history for this channel.');
					return;
				}
				if (new Date(message.timestamp) < lastSuccessDate) {
					console.log(`History: Unable to recover all history.  Some messages are older than ${lastSuccessDate}.`);
				}
				channel.history({'direction' : 'forwards', 'start' : lastSuccessDate.getTime()}).then((paginatedResult) => {
					paginatedResult.first(that.getNextPage.bind({'context' : that, 'history' : history, 'resolve' : resolve}));
				});
			}).catch (error => {
				RTPaaSError.triggerAblyError(error);
				that.sendErrorMessage("retrieveHistoryFromDate() "+ error.message+" fileName="+error.filename
					+" line#="+error.lineno+" colno="+error.colno, (new Error()).stack);
			});
		});
	}

	//recursively gets every available page of ably history
	getNextPage(err, nextPage) {
		if(err) {
			return;
		}

		let history = this.history;
		$(nextPage.items).each(function() {
			if([Constants.MessageNames.CHAT,
				Constants.MessageNames.HAND,
				Constants.MessageNames.CALL,
				Constants.MessageNames.BROADCAST,
				Constants.MessageNames.LOCK].includes(this.data.messageName)) {
				history.push(this);
			}
		});
		if(!nextPage.isLast()) {
			nextPage.next(this.context.getNextPage.bind(this));
		} else {
			this.resolve(history);
		}
	}

	replayHistory(context, history) {
		return new Promise((resolve) => {
			for(let message of history) {
				message.data.isHistorical = true;
				if(Constants.MessageNames.CHAT === message.data.messageName) {
					if(this.isProctor() && (Constants.EventTypes.INITIATE_CHAT === message.data.eventType || Constants.EventTypes.END_CHAT === message.data.eventType)) {
						continue;
					}
					context.handleChatMessage(message);
				} else if(Constants.MessageNames.HAND === message.data.messageName) {
					context.handleHandMessage(message);
				} else if (Constants.MessageNames.CALL === message.data.messageName) {
					let testerHistoryEventTypes = [Constants.EventTypes.END_CALL, Constants.EventTypes.UNANSWERED_CALL, Constants.EventTypes.TESTER_LEFT_CALL, Constants.EventTypes.TESTER_FAILED_TO_CONNECT_CALL, Constants.EventTypes.TESTER_NOT_IN_CALL];
					if((context.isProctor() && context.isMessageSentFromDifferentProctor(message)) || (Constants.Role.TESTER === message.data.senderType && testerHistoryEventTypes.includes(message.data.eventType))) {
						context.handleCallMessage(message);
					}
				} else if (Constants.MessageNames.LOCK === message.data.messageName) {
					context.handleLockMessage(message);
				}
			}
			resolve();
		});
	}

	isProctorPresent(channel) {
		return new Promise((resolve, reject) => {
			channel.presence.get((err, members) => {
				if (err) {
					reject(err);
				}
				let isProctorPresent = false;
				if (members) {
					for (let member of members) {
						if (member.data.isProctor) {
							isProctorPresent = true;
							break;
						}
					}
				}
				resolve(isProctorPresent);
			});
		});
	}

	isSpecificProctorPresent(channel, clientId) {
		return new Promise((resolve, reject) => {
			channel.presence.get((err, members) => {
				if (err) {
					reject(err);
				}
				let isSpecificProctorPresent = false;
				if (members) {
					for (let member of members) {
						if (member.clientId === clientId) {
							isSpecificProctorPresent = true;
							break;
						}
					}
				}
				resolve(isSpecificProctorPresent);
			});
		});
	}

	isTesterPresent(channel) {
		return new Promise((resolve, reject) => {
			channel.presence.get((err, members) => {
				if (err) {
					reject(err);
				}
				let isTesterPresent = false;
				if (members) {
					for (let member of members) {
						if (!member.data.isProctor) {
							isTesterPresent = true;
							break;
						}
					}
				}
				resolve(isTesterPresent);
			});
		});
	}

	isMessageSentFromTester(message) {
		return message.data.senderType === Constants.Role.TESTER;
	}

	startCallChime() {
		if (this.receivingCallAudio) {
			this.receivingCallAudio.loop = true;
			this.receivingCallAudio.play();
		}
	}

	stopCallChime() {
		if (this.receivingCallAudio) {
			this.receivingCallAudio.pause();
			this.receivingCallAudio.currentTime = 0;
		}
	}

	setIsGroupAudioCallRunning(flag){
		this.isGroupAudioCallRunning = flag;
	}

	getIsGroupAudioCallRunning(){
		return Boolean(this.isGroupAudioCallRunning);
	}

	static createMessage(payload) {
		let message = Object.create(new Ably.Realtime.Message());
		message.name = payload.messageName;
		message.data = payload;
		return message;
	}

	cleanupController(testerIdentifier){
		// Clear the publish stats interval
		if (this.publishStatsInterval) {
			clearInterval(this.publishStatsInterval);
			this.publishStatsInterval = null;
		}
		// Unsubscribe and Detach ably Channels
		this.createOrGetChannel(testerIdentifier).unsubscribe();
		this.createOrGetChannel(testerIdentifier).detach();
		this.getChannelWithHistory(testerIdentifier).unsubscribe();
		this.getChannelWithHistory(testerIdentifier).detach();
		this.ably?.connection.off(); /* remove all event listeners */
		this.stopCallChime();
	}
}
